import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, Timestamp, increment } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { generateInitialsAvatar } from '../utils/avatarUtils';

type UsernameHistory = {
  username: string;
  changedAt: Date;
};

type ProfileSettings = {
  username: string;
  usernameHistory: UsernameHistory[];
  description: string;
  profilePicture: string;
  country: string;
  city: string;
  socialLinks: Array<{ platform: string; url: string }>;
  mobileMoneyAccounts: Array<{ type: string; number: string }>;
  externalPhotos: Array<{
    id: string;
    url: string;
    title?: string;
    description?: string;
  }>;
  showExternalPhotos: boolean;
  likes: number;
  referralCode: string;
  amountGiven: number;
};

const defaultSettings: ProfileSettings = {
  username: '',
  usernameHistory: [],
  description: '',
  profilePicture: '',
  country: '',
  city: '',
  socialLinks: [],
  mobileMoneyAccounts: [],
  externalPhotos: [],
  showExternalPhotos: true,
  likes: 0,
  referralCode: '',
  amountGiven: 0
};

export function useProfileSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ProfileSettings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  // Charger les paramètres au montage du composant
  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.id) return;
      
      try {
        const [userDoc, profileSettingsDoc] = await Promise.all([
          getDoc(doc(db, 'users', user.id)),
          getDoc(doc(db, 'profileSettings', user.id))
        ]);

        // Code conservé mais commenté temporairement
        /*
        const savedProfilePicture = localStorage.getItem(`user_${user.id}_profilePicture`);
        */
        
        // Récupérer les photos depuis localStorage
        let savedPhotosData = { showExternalPhotos: true, externalPhotos: [] };
        try {
          const savedPhotos = localStorage.getItem(`user_${user.id}_photos`);
          if (savedPhotos) {
            savedPhotosData = JSON.parse(savedPhotos);
          }
        } catch (error) {
          console.error('Erreur lors du parsing des photos:', error);
        }

        const userData = userDoc.exists() ? userDoc.data() : {};
        const profileData = profileSettingsDoc.exists() ? profileSettingsDoc.data() : {};

        // Utiliser uniquement l'avatar par défaut pour le moment
        const defaultAvatar = generateInitialsAvatar(userData.username || '');
        
        // Fusionner les données de Firestore et localStorage
        const mergedSettings = {
          ...defaultSettings,
          ...profileData,
          ...userData,
          // Définir l'avatar par défaut comme photo de profil
          profilePicture: defaultAvatar,
          // Utiliser les données de localStorage pour les photos
          showExternalPhotos: savedPhotosData.showExternalPhotos,
          externalPhotos: savedPhotosData.externalPhotos || []
        };

        // Si aucune photo n'est trouvée dans localStorage, essayer de récupérer depuis Firestore
        if (!mergedSettings.externalPhotos || mergedSettings.externalPhotos.length === 0) {
          if (profileData.externalPhotos && profileData.externalPhotos.length > 0) {
            mergedSettings.externalPhotos = profileData.externalPhotos;
          }
        }

        setSettings(mergedSettings);
        setIsLoaded(true);
        
        // Mettre à jour le localStorage avec les données fusionnées
        localStorage.setItem(`user_${user.id}_photos`, JSON.stringify({
          showExternalPhotos: mergedSettings.showExternalPhotos,
          externalPhotos: mergedSettings.externalPhotos
        }));
      } catch (error) {
        console.error('Erreur de chargement des paramètres:', error);
        setIsLoaded(true);
      }
    };

    loadSettings();
  }, [user?.id]);

  const updateSettings = async (updates: Partial<ProfileSettings>) => {
    // Si la mise à jour concerne la photo de profil, l'ignorer temporairement
    if (updates.profilePicture) {
      // Ignorer les changements de photo de profil, mais conserver le reste des mises à jour
      const { profilePicture, ...otherUpdates } = updates;
      
      // Pour les autres mises à jour
      const newSettings = { ...settings, ...otherUpdates };
      setSettings(newSettings);
      
      // Code commenté temporairement pour conservation future
      /*
      if (profilePicture.startsWith('blob:')) {
        try {
          const response = await fetch(profilePicture);
          const blob = await response.blob();
          const reader = new FileReader();
          
          const base64 = await new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
          
          // Sauvegarder immédiatement en localStorage
          if (user?.id) {
            localStorage.setItem(`user_${user.id}_profilePicture`, base64);
          }
          
          // Mettre à jour le state avec l'image en base64
          setSettings(prev => ({
            ...prev,
            ...updates,
            profilePicture: base64
          }));
        } catch (error) {
          console.error('Erreur lors de la conversion de l\'image:', error);
        }
      }
      */
    } else {
      // Pour les autres mises à jour
      const newSettings = { ...settings, ...updates };
      setSettings(newSettings);
      
      // Si la mise à jour concerne les photos, mettre à jour le localStorage immédiatement
      if (updates.externalPhotos || updates.showExternalPhotos !== undefined) {
        if (user?.id) {
          localStorage.setItem(`user_${user.id}_photos`, JSON.stringify({
            showExternalPhotos: newSettings.showExternalPhotos,
            externalPhotos: newSettings.externalPhotos
          }));
        }
      }
    }
  };

  const saveSettings = async () => {
    if (!user?.id) return false;

    try {
      // Sauvegarder dans users
      const userRef = doc(db, 'users', user.id);
      await setDoc(userRef, {
        username: settings.username,
        description: settings.description,
        socialLinks: settings.socialLinks,
        country: settings.country,
        city: settings.city,
        amountGiven: settings.amountGiven,
        showExternalPhotos: settings.showExternalPhotos,
        updatedAt: Timestamp.fromDate(new Date())
      }, { merge: true });

      // Préparer les données à sauvegarder
      const settingsToSave = {
        ...settings,
        profilePicture: settings.profilePicture ? 'exists' : '',
        externalPhotos: settings.externalPhotos,
        showExternalPhotos: settings.showExternalPhotos,
        lastUpdated: new Date()
      };

      // Sauvegarder dans profileSettings
      const profileSettingsRef = doc(db, 'profileSettings', user.id);
      await setDoc(profileSettingsRef, settingsToSave, { merge: true });
      
      // Sauvegarder les photos dans localStorage
      localStorage.setItem(`user_${user.id}_photos`, JSON.stringify({
        showExternalPhotos: settings.showExternalPhotos,
        externalPhotos: settings.externalPhotos
      }));

      // Forcer la mise à jour du contexte
      await updateDoc(userRef, { 
        lastUpdated: new Date(),
        profileVersion: increment(1) // Ajouter un compteur de version
      });

      return true;
    } catch (error) {
      console.error('Erreur de sauvegarde:', error);
      return false;
    }
  };

  return { settings, updateSettings, saveSettings, isLoaded };
}